'use client';

import * as React from 'react';
import { FC } from 'react';
import { cn } from '../lib/utils';
import { HOME_QUOTES, shuffled } from '@puzzlio/core';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@puzzlio/ui';

/** Players' words, one at a time in a speech bubble. */
export const QuotesSection: FC<{ className?: string }> = ({ className }) => {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [quotes, setQuotes] = React.useState(HOME_QUOTES);

  // Shuffled after hydration, since a shuffle during render would differ from the server's HTML.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  React.useEffect(() => setQuotes(shuffled(HOME_QUOTES)), []);

  React.useEffect(() => {
    if (!api) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <div className={cn('relative rounded-xl border-[3px] border-black bg-blue-300 px-6 pt-6 pb-5', className)}>
      <Carousel opts={{ loop: true }} setApi={setApi}>
        <CarouselContent>
          {quotes.map((quote, index) => (
            <CarouselItem key={index} className="flex flex-col items-center justify-center gap-2 pb-0 text-center">
              <p className="font-quicksand text-lg leading-[1.25] font-bold">“{quote.content}”</p>
              <p className="font-quicksand text-sm leading-snug font-semibold">{quote.author}</p>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      <div className="mt-4 flex justify-center gap-1.5">
        {quotes.map((_, index) => (
          <button
            key={index}
            onClick={() => api?.scrollTo(index)}
            className={cn('size-2 rounded-full', current === index ? 'bg-white' : 'bg-blue-600')}
            aria-label={`Go to quote ${index + 1}`}
          />
        ))}
      </div>
      {/* The bubble's tail. Drawn at ¾ of the artwork's size so its stroke matches the bubble's 3px border,
          and pulled up over that border so its fill breaks the border where the two meet. */}
      <svg
        viewBox="0 0 29 30"
        aria-hidden
        className="absolute top-[calc(100%-3px)] right-[22%] h-[22.5px] w-[21.75px] overflow-visible"
      >
        <path d="M5.63809 25L29 0H0L5.63809 25Z" fill="var(--blue-300)" />
        <path d="M25 6L5.94663 25L2 6" strokeWidth="4" strokeLinecap="round" stroke="var(--black)" fill="none" />
      </svg>
    </div>
  );
};
